// React component to display the status update input

import React from 'react';
import TwSelect, { TwSelectOption } from '../../../TwSelects/TwSelects';
import useNotifications from '../../../../providers/Notifications/Notifications.provider';
import { logRocketTrack } from '../../../../utils/logRocketUtils';
import useAuth from '../../../../providers/Auth/Auth.provider';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { nanoid } from '@reduxjs/toolkit';
import { setLoadCalendarReRenderId } from '../../../../redux/features/orders/ordersSlice';
import {
  StopStatus,
  StopStatusColor,
  TRIP_STATUS,
  TripStop
} from '../../../../redux/api/trips/trips.model';
import useStopStatusProgression from '../../../../hooks/useStopStatusProgression';
import { setTripDetailReload } from '../../../../redux/features/navigation/navigationSlice';
import { useLocation } from 'react-router-dom';
import RTKUtils from '../../../../utils/rtkUtils';

export interface StopStatusUpdateInputProps {
  /**
   * stop id of the stop
   * */
  stop: TripStop;

  /**
   * trip status for RTK invalidation
   * */
  tripStatus: TRIP_STATUS;
  /**
   *
   * Trip id for RTK invalidation
   */
  tripId?: string;
  /**
   * show Background color depending on stop status
   */
  showBackgroundColor?: boolean;

  reloadData?: () => void;
  setLoading?: (loading: boolean) => void;
}

const options: TwSelectOption[] = [
  { label: 'Not started', value: StopStatus.notStarted },
  { label: 'In Transit', value: StopStatus.inTransit },
  { label: 'Arrived', value: StopStatus.arrived },
  { label: 'Complete', value: StopStatus.complete }
];

const StopStatusUpdateInput: React.FC<StopStatusUpdateInputProps> = ({
  stop,
  tripStatus,
  tripId,
  showBackgroundColor = false,
  reloadData,
  setLoading
}): JSX.Element => {
  const {
    data: { userData }
  } = useAuth();
  const { addNotification } = useNotifications();
  const dispatch = useAppDispatch();
  const route = useLocation();
  const isCalendarOpen = useAppSelector((state) => state.navigation.isCalendarOpen);
  const isLoadDetailOpen = useAppSelector((state) => state.navigation.isLoadDetailOpen);
  const { updateStopStatus } = useStopStatusProgression();
  const status = stop.status || StopStatus.notStarted;

  const handleChange = (e: any): void => {
    const newStatus = e.target.value as StopStatus;

    if (!stop?.id || !stop?.updated_at) {
      logRocketTrack('Status update', {
        action: 'Change stop status',
        severity: 'high',
        tenant_id: userData?.tenant?.id,
        company_name: userData?.tenant?.company_name,
        email: userData?.email_address,
        user_first_name: userData?.first_name,
        user_last_name: userData?.last_name
      });
      addNotification({
        message: 'Stop not found, please try again',
        variant: 'error'
      });
    } else {
      const tagsToInvalidate = RTKUtils.invalidateTagsForOrdersAndTrips(route.pathname);
      updateStopStatus(stop.id, newStatus, tripStatus, tagsToInvalidate, stop.updated_at)
        .then((res) => {
          if (isCalendarOpen) {
            const reRenderId = nanoid();
            dispatch(setLoadCalendarReRenderId(reRenderId));
          }
          if (isLoadDetailOpen) {
            dispatch(setTripDetailReload(true));
          }

          setLoading && reloadData && setLoading(true);
          setTimeout(() => {
            reloadData && reloadData();
          }, 1800);
        })
        .catch((err) => {
          logRocketTrack('Status update', {
            action: 'Change stop status',
            severity: 'high',
            tenant_id: userData?.tenant?.id,
            company_name: userData?.tenant?.company_name,
            email: userData?.email_address,
            user_first_name: userData?.first_name,
            user_last_name: userData?.last_name
          });

          let message = 'Error updating stop status, please try again';
          let hasReloadAction = false;

          if (err.status && err.status >= 400 && err.status < 500 && err.data?.message) {
            message = err.data.message;
            // Check if message contains "reload" (case insensitive)
            hasReloadAction = message.toLowerCase().includes('reload');
          } else {
            // log the error
            console.log('Unexpected error', err);
          }

          const reloadPage = (): void => {
            window.location.reload();
          };

          const notificationObj = {
            message,
            variant: 'error' as const,
            ...(hasReloadAction && {
              actionOptions: {
                action: 'reload',
                onActionClick: (): void => {
                  reloadPage();
                }
              }
            })
          };

          addNotification(notificationObj);
        });
    }
  };

  return (
    <TwSelect
      id="stopStatusUpdateInput"
      name="stopStatusUpdateInput"
      value={status}
      optionList={options}
      onInputChange={handleChange}
      error={false}
      selectSx={{
        '& .MuiInputBase-input': {
          backgroundColor: showBackgroundColor
            ? `${StopStatusColor[status]} !important`
            : 'transparent !important',
          color: showBackgroundColor ? '#fff !important' : '#3A4DE9 !important',
          padding: '1px 20px 1px 8px !important',
          fontSize: showBackgroundColor ? '12px' : '14px',
          fontWeight: showBackgroundColor ? '500 !important' : 'normal',
          borderRadius: showBackgroundColor ? '4px !important' : 'none'
        },
        '.MuiSvgIcon-root': {
          color: showBackgroundColor ? '#fff !important' : '#9CA3AF !important',
          right: '4px !important'
        },
        '& .MuiOutlinedInput-notchedOutline': { border: 'none' }
      }}
    />
  );
};

export default StopStatusUpdateInput;
